/* @flow */

import * as React from 'react';
import Alert from '@mui/material/Alert';

import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';

import styles from './NotificationBanner.style';
import BaseLink from 'src/shared/ui/component/link/BaseLink';
import ConditionalWrapper from 'src/shared/ui/component/misc/ConditionalWrapper';
import CheckIcon from 'src/shared/ui/component/icon/CheckIcon';
import WarningOutlineIcon from 'src/shared/ui/component/icon/WarningOutlineIcon';

type Props = {
  withWarning?: boolean,
  withError?: boolean,
  href?: string,
  icon?: React.Node,
  handleDismiss?: () => void,
  children: React.Node,
  action?: React.Node,
  isCentered?: boolean,
};

const useStyles = createUseThemeStyles(styles);

const NotificationBanner = ({
  withWarning,
  withError,
  icon,
  href,
  handleDismiss,
  children,
  action,
  isCentered,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */
  const classes = useStyles({
    withLink: !!href,
    isDismissable: !!handleDismiss,
    isCentered,
  });

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const getSeverity = () => {
    if (withError) {
      return 'error';
    }
    if (withWarning) {
      return 'warning';
    }
    return 'success';
  };

  const getIcon = () => {
    if (icon) return icon;

    if (withError) {
      return null;
    }

    if (withWarning) {
      return <WarningOutlineIcon />;
    }

    return <CheckIcon className={classes.checkIcon} />;
  };

  return (
    <>
      <Alert
        className={classes.alert}
        severity={getSeverity()}
        onClose={handleDismiss}
        icon={getIcon()}
        action={action}
        classes={{
          message: classes.message,
          icon: classes.icon,
        }}
      >
        <ConditionalWrapper
          condition={!!href}
          wrapper={(children) => (
            <BaseLink className={classes.link} href={href}>
              {children}
            </BaseLink>
          )}
        >
          <p className={classes.text}>{children}</p>
        </ConditionalWrapper>
      </Alert>
    </>
  );
};

export default React.memo<Props>(NotificationBanner);
